/* istanbul ignore file */
import { NotificationModel } from 'pages/Consultation';
import { useRef } from 'react';
import { convertFloat32ToInt16 } from '../../util/audioUtils';
import { RecordingStatus } from './LiveTranscription';

interface UseAudioProcessor {
  startAudio: (onProcess: (audioBuffer: Int16Array) => void) => Promise<void>;
  stopAudio: () => void;
  bufferSize: number;
}

export const useAudioProcessor = (
  sampleRateHertz: number,
  setNotification: React.Dispatch<React.SetStateAction<NotificationModel | null>>,
  setRecording: React.Dispatch<React.SetStateAction<RecordingStatus>>,
): UseAudioProcessor => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const bufferSize = 4096;

  const startAudio = async (onProcess: (audioBuffer: Int16Array) => void) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new AudioContext({ sampleRate: sampleRateHertz });
      const mediaStreamSource = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
      audioContextRef.current = audioContext;
      mediaStreamRef.current = stream;
      processorRef.current = processor;
      mediaStreamSource.connect(processor);
      processor.connect(audioContext.destination);

      processor.onaudioprocess = event => {
        const float32Data = event.inputBuffer.getChannelData(0);
        const int16Data = convertFloat32ToInt16(float32Data);
        onProcess(int16Data);
      };
    } catch (error) {
      setRecording(RecordingStatus.ERROR);
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        setNotification({
          textContent: 'There is a problem. Allow access to the microphone to start the transcript recording',
          state: 'urgent',
        });
      } else {
        setNotification({ textContent: 'Audio error', state: 'urgent' });
      }
    }
  };

  const stopAudio = () => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }
  };

  return { startAudio, stopAudio, bufferSize };
};
